import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { frozenStateReconciler } from "../../../Utils/persistHelpers"
import { logout } from "../../LoginPage/redux/auth.actions"
import { preferencesSuccess } from "../../User/redux/user.actions"
import { ADMIN_USERS_SLICE_NAME } from "./adminUsers.constants"
import {
  deleteAdminUser,
  getAdminUsers,
  getAdminUsersCount,
  getAdminUsersExport,
  getAdminUsersInviteRoles,
  inviteAdminUser,
  resendAdminUserInvite,
  updateAdminUserRole
} from "./adminUsers.thunks"

/* ------------- Initial State ------------- */

const columnsAdmin = [
  { field_id: "admin_full_name", label: "Admin full name", active: true },
  { field_id: "email", label: "Admin email", active: true },
  { field_id: "invite_sent", label: "Invite sent", active: true },
  { field_id: "last_2fa_access", label: "Last 2FA access", active: true },
  { field_id: "last_activity", label: "Last activity", active: true },
  { field_id: "role", label: "Role", active: true }
]

const initialState = {
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  statistics: {
    count: 0
  },

  inviteRoles: [],

  exportFetching: false,
  exportResult: null,

  saveFetching: false,
  saveResult: null,

  resendResult: null,

  deleteFetching: false,
  deleteResult: null,

  filters: {
    status: "all",
    admin_full_name: [],
    email: [],
    exclude_fields: [],
    invite_sent: null,
    last_2fa_access: null,
    last_activity: null,
    role: "all"
  },
  visibleFilters: {
    status: true,
    admin_full_name: true,
    email: true,
    invite_sent: false,
    last_2fa_access: false,
    last_activity: false,
    role: true
  },

  defaultColumnsAdmin: [{ field_id: "status", label: "Status" }, ...columnsAdmin],
  columnsAdmin: [...columnsAdmin]
}

const slice = createSlice({
  name: ADMIN_USERS_SLICE_NAME,
  initialState,
  reducers: {
    // Legacy call sites dispatched this with two arguments — `adminUsersSetProp(key, value)`.
    // RTK actions carry a single payload, so callers pass `adminUsersSetProp({ key, value })`,
    // matching the `businessAccountsSetProp`/`alertsSetProp` slices.
    adminUsersSetProp(state, action) {
      const { key, value } = action.payload

      if (["filters", "visibleFilters"].includes(key)) {
        state[key] = { ...state[key], ...value }
      } else {
        state[key] = value
      }
    },
    clearAdminUsersFilters(state) {
      state.filters = initialState.filters
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- admin users list ---------------- */
      .addCase(getAdminUsers.pending, (state) => {
        state.fetching = true
      })
      .addCase(getAdminUsers.fulfilled, (state, action) => {
        state.fetching = false
        state.result = action.payload
      })
      .addCase(getAdminUsers.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- statistics ---------------- */
      .addCase(getAdminUsersCount.fulfilled, (state, action) => {
        state.statistics = action.payload
      })

      /* ---------------- invite roles ---------------- */
      .addCase(getAdminUsersInviteRoles.fulfilled, (state, action) => {
        state.inviteRoles = action.payload
      })

      /* ---------------- export ---------------- */
      .addCase(getAdminUsersExport.pending, (state) => {
        state.exportFetching = true
      })
      .addCase(getAdminUsersExport.fulfilled, (state, action) => {
        state.exportFetching = false
        state.exportResult = action.payload
      })
      .addCase(getAdminUsersExport.rejected, (state) => {
        state.exportFetching = false
      })

      /* ---------------- update role ---------------- */
      .addCase(updateAdminUserRole.pending, (state) => {
        state.saveFetching = true
        state.saveResult = null
      })
      .addCase(updateAdminUserRole.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = action.payload || true
      })
      .addCase(updateAdminUserRole.rejected, (state) => {
        state.saveFetching = false
      })

      /* ---------------- invite ---------------- */
      .addCase(inviteAdminUser.pending, (state) => {
        state.saveFetching = true
        state.saveResult = null
      })
      .addCase(inviteAdminUser.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = action.payload || true
      })
      .addCase(inviteAdminUser.rejected, (state) => {
        state.saveFetching = false
      })

      /* ---------------- resend invite ---------------- */
      .addCase(resendAdminUserInvite.pending, (state) => {
        state.resendResult = null
      })
      .addCase(resendAdminUserInvite.fulfilled, (state, action) => {
        state.resendResult = action.payload || true
      })

      /* ---------------- delete ---------------- */
      .addCase(deleteAdminUser.pending, (state) => {
        state.deleteFetching = true
        state.deleteResult = null
      })
      .addCase(deleteAdminUser.fulfilled, (state, action) => {
        state.deleteFetching = false
        state.deleteResult = action.payload || true
      })
      .addCase(deleteAdminUser.rejected, (state) => {
        state.deleteFetching = false
      })

      /* ---------------- logout ---------------- */
      .addCase(logout, () => initialState)

      /* ---------------- restore saved preferences ---------------- */
      .addCase(preferencesSuccess, (state, { payload }) => {
        const preference = payload?.["admin-users"]?.data
        if (!preference || preference.version !== ADMIN_USERS_PREFERENCES_VERSION) return
        if (!preference.filters || !preference.columnsAdmin || !preference.visibleFilters) return

        state.filters = preference.filters
        state.columnsAdmin = preference.columnsAdmin
        state.visibleFilters = preference.visibleFilters
      })
  }
})

export const adminUsersActions = slice.actions

/* ------------- Selectors ------------- */

export const selectAreFiltersDefault = (state) =>
  isEqual(state.adminUsers.filters, initialState.filters)

// Bump when the shape of the saved `{ version, filters, columnsAdmin, visibleFilters }`
// payload changes.
export const ADMIN_USERS_PREFERENCES_VERSION = 1

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: "adminUsers",
    storage,
    // Only the user's own table setup survives a reload — the users list, statistics, invite
    // roles, and export/save/resend/delete results are all re-fetched or transient.
    whitelist: ["filters", "visibleFilters", "columnsAdmin"],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
